import React, { createContext, useContext } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import type { ModeOverrides, ViewPresentation } from '../types/schema';
import type { BlockOverrides } from '../types/block';
import type { FormCustomRenderer } from '../types/renderer';
import type { FormEngineStatus } from '../store/engineReducer';
import type { FormPrecisionConfig } from '../precision/precisionContract';
import type { ProgrammaticWriteGuardRef } from '../processors/programmaticWriteGuard';

export interface FormEngineStateContextValue {
  meta: Record<string, unknown>;
  modeOverrides?: ModeOverrides;
  blockOverrides?: BlockOverrides;
  isReadOnly: boolean;
  viewPresentation: ViewPresentation;
  customRenderers?: Record<string, FormCustomRenderer>;
  getOptionProp: (field: string, prop: string) => unknown;
  setOptionMeta: (fieldName: string, value: string, metadata: Record<string, unknown>) => void;
  status: FormEngineStatus;
  hideRequiredMarker?: boolean;
  /** Context params for data fetching and view-mode masking (ViewField MaskedValue uses params.id). */
  params?: Record<string, string>;
  /** Compiled numeric precision contract from domain.json. */
  precisionConfig?: FormPrecisionConfig;
}

export interface FormEngineFormContextValue {
  form: UseFormReturn<Record<string, unknown>>;
  udfRequiredFieldsRef: React.RefObject<Map<string, string>>;
  /** True while the form is being hydrated from API data. Consumers must skip destructive side effects (e.g. clearOnHide) during this window. */
  hydratingRef: React.RefObject<boolean>;
  /** Monotonic counter incremented after each hydration cycle completes. Consumers compare against a local snapshot to detect hydration-induced value changes even after hydratingRef is cleared. */
  hydrationEpochRef?: React.RefObject<number>;
  programmaticWriteGuard?: ProgrammaticWriteGuardRef;
}

const FormEngineStateContext = createContext<FormEngineStateContextValue | null>(null);
const FormEngineFormContext = createContext<FormEngineFormContextValue | null>(null);

export function useFormEngineState(): FormEngineStateContextValue {
  const ctx = useContext(FormEngineStateContext);
  if (!ctx) throw new Error('useFormEngineState must be used within FormEngineProvider');
  return ctx;
}

export function useFormEngineForm(): FormEngineFormContextValue {
  const ctx = useContext(FormEngineFormContext);
  if (!ctx) throw new Error('useFormEngineForm must be used within FormEngineProvider');
  return ctx;
}

interface FormEngineProviderProps {
  stateValue: FormEngineStateContextValue;
  formValue: FormEngineFormContextValue;
  children: React.ReactNode;
}

export function FormEngineProvider({ stateValue, formValue, children }: FormEngineProviderProps): React.ReactElement {
  return (
    <FormEngineStateContext.Provider value={stateValue}>
      <FormEngineFormContext.Provider value={formValue}>
        {children}
      </FormEngineFormContext.Provider>
    </FormEngineStateContext.Provider>
  );
}
